import { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '@/context/AuthContext'
import { Loader2 } from 'lucide-react'
import { useToast } from '@/components/ui/use-toast'
import { useTeamSlug } from '@/context/TeamSlugContext'
import { supabase } from '@/integrations/supabase/client'

const PublicAuthCallback = () => {
	const navigate = useNavigate()
	const location = useLocation()
	const { session, loading } = useAuth()
	const { toast } = useToast()
	const { isPortal, teamSlug } = useTeamSlug()
	const [isProcessing, setIsProcessing] = useState(true)

	useEffect(() => {
		const handleAuthCallback = async () => {
			// Check if we have auth tokens in the URL hash (Supabase returns them this way)
			if (location.hash && location.hash.includes('access_token')) {
				// Let Supabase handle the hash and set the session
				// This happens automatically in the AuthContext, but we need to wait for it
				await new Promise((resolve) => setTimeout(resolve, 2000))
			}

			// Wait for auth to fully settle
			if (loading) {
				return
			}

			// Get the latest session after processing
			const {
				data: { session: currentSession },
			} = await supabase.auth.getSession()

			if (currentSession?.user) {
				// Get the return URL from session storage or default to roadmap
				const returnUrl = sessionStorage.getItem('auth_return_url')

				sessionStorage.removeItem('auth_return_url')

				// Determine where to redirect based on portal type and saved URL
				let redirectTo = '/roadmap'

				if (returnUrl) {
					redirectTo = returnUrl
				} else if (isPortal) {
					redirectTo = '/roadmap'
				} else if (teamSlug) {
					redirectTo = `/c/${teamSlug}/roadmap`
				} else {
					// If no team slug, try to extract from the return URL or referrer
					const savedTeamSlug = sessionStorage.getItem('auth_team_slug')
					if (savedTeamSlug) {
						redirectTo = `/c/${savedTeamSlug}/roadmap`
						sessionStorage.removeItem('auth_team_slug')
					}
				}

				toast({
					title: 'Welcome!',
					description: 'You have successfully signed in.',
				})

				navigate(redirectTo, { replace: true })
			} else {
				// Check if we're still processing the hash
				if (location.hash && location.hash.includes('access_token')) {
					// Wait a bit more
					await new Promise((resolve) => setTimeout(resolve, 1000))
					return
				}

				// No session after callback, something went wrong
				toast({
					variant: 'destructive',
					title: 'Authentication Failed',
					description: 'Unable to complete sign in. Please try again.',
				})

				// Redirect to an appropriate page
				const redirectTo = isPortal ? '/roadmap' : '/'
				navigate(redirectTo, { replace: true })
			}

			setIsProcessing(false)
		}

		handleAuthCallback()
	}, [location.hash, session, loading, navigate, toast, isPortal, teamSlug])

	return (
		<div className="flex h-screen w-full items-center justify-center">
			<div className="text-center">
				<Loader2 className="h-8 w-8 animate-spin text-muted-foreground mx-auto mb-4" />
				<p className="text-muted-foreground">Completing sign in...</p>
				{isProcessing && location.hash && (
					<p className="text-xs text-muted-foreground mt-2">
						Processing authentication...
					</p>
				)}
			</div>
		</div>
	)
}

export default PublicAuthCallback
